package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLTextAreaElement

private data class PromptVariable(val token: String, val label: String, val description: String)

private val PROMPT_VARIABLES = listOf(
    PromptVariable("{{workspace_name}}", "Workspace", "Workspace display name."),
    PromptVariable("{{team_name}}", "Standup", "Standup name (e.g. \"Platform\")."),
    PromptVariable("{{yesterday_items}}", "Yesterday", "Bullet list of yesterday's Linear activity per user."),
    PromptVariable("{{today_items}}", "Today", "Bullet list of in-progress / planned items per user."),
    PromptVariable("{{blockers}}", "Blockers", "Issues flagged with the blocker label."),
    PromptVariable(
        "{{since_label}}",
        "Since label",
        "Smart label for the activity window — \"yesterday\" / \"since Friday\" / \"since Apr 18\". " +
            "Use instead of the literal word \"yesterday\".",
    ),
    PromptVariable("{{since_date}}", "Since date", "ISO date the activity window starts from."),
    PromptVariable("{{since_days_back}}", "Days back", "Integer number of days the window covers."),
    PromptVariable(
        "{{today_events}}",
        "Today's events",
        "Calendar events for the standup date — formatted bullet list.",
    ),
)

private val YESTERDAY_WORD_REGEX = Regex("\\byesterday\\b", RegexOption.IGNORE_CASE)

internal const val PROMPT_TEXTAREA_ID = "sb-template-prompt-body"

@Composable
internal fun PromptBodyCard(
    promptBody: String,
    onPromptBodyChange: (String) -> Unit,
    enabled: Boolean,
    onInsertVariable: (String) -> Unit,
) {
    val charCount = promptBody.length
    val varCount = countVariables(promptBody)
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().padding(Spacing.lg).gap(Spacing.md)) {
            CardSectionHeader(
                title = "Prompt body",
                subtitle = "Preview only: the prompt text isn't sent to the AI yet. Use variables for dynamic content.",
            )

            if (
                YESTERDAY_WORD_REGEX.containsMatchIn(promptBody) &&
                !promptBody.contains("{{since_label}}")
            ) {
                SpanText(
                    text = "Tip: use {{since_label}} instead of the word 'yesterday' so the preview reads " +
                        "naturally after a holiday (e.g. 'since Friday').",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.warning),
                )
            }

            VariableChips(enabled = enabled, onInsert = onInsertVariable)

            TextArea(
                attrs = Modifier
                    .width(100.percent)
                    .backgroundColor(Palette.surface)
                    .color(Palette.textPrimary)
                    .borderRadius(Radius.md)
                    .fontFamily(*Typography.MONO_STACK)
                    .styleModifier {
                        property("padding", "14px 16px")
                        property("font-size", "13.5px")
                        property("line-height", "1.6")
                        property("border", "1px solid ${Palette.border.cssRgb()}")
                        property("outline", "none")
                        property("resize", "vertical")
                        property("min-height", "440px")
                        property("transition", Motion.TRANSITION_BASE)
                    }
                    .toAttrs {
                        this.value(promptBody)
                        this.placeholder(
                            "Write your template body. Insert variables above " +
                                "or type {{workspace_name}} directly.",
                        )
                        this.onInput { onPromptBodyChange(it.value) }
                        attr("rows", "22")
                        attr("id", PROMPT_TEXTAREA_ID)
                        attr("aria-label", "Prompt body")
                        if (!enabled) attr("disabled", "disabled")
                    },
            )

            Row(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("gap", "16px")
                    property("flex-wrap", "wrap")
                },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                MetaChip(text = "$charCount chars")
                MetaChip(text = if (varCount == 1) "1 variable" else "$varCount variables")
                Box(modifier = Modifier.styleModifier { property("flex", "1") })
                if (charCount == 0 && enabled) {
                    SpanText(
                        text = "Tip: start with a preset above, or duplicate the default template.",
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.textTertiary),
                    )
                }
            }
        }
    }
}

@Composable
private fun VariableChips(enabled: Boolean, onInsert: (String) -> Unit) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
        SpanText(
            text = "Variables — click to insert at cursor",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary)
                .styleModifier {
                    property("text-transform", "none")
                    property("letter-spacing", "0.06em")
                },
        )
        Row(
            modifier = Modifier.styleModifier {
                property("gap", "6px")
                property("flex-wrap", "wrap")
            },
        ) {
            PROMPT_VARIABLES.forEach { v ->
                VariableChip(label = v.token, hint = v.description, enabled = enabled) {
                    onInsert(v.token)
                }
            }
        }
    }
}

@Composable
private fun VariableChip(label: String, hint: String, enabled: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .styleModifier {
                property("gap", "4px")
                property("background-color", Palette.surfaceSubtle.cssRgb())
                property("border-radius", "999px")
                property("padding", "4px 10px")
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("transition", Motion.TRANSITION_HOVER)
                if (enabled) property("cursor", "pointer")
            }
            .then(if (enabled) Modifier.onClick { onClick() } else Modifier),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontFamily(*Typography.MONO_STACK)
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(if (enabled) Palette.accent else Palette.textTertiary)
                .styleModifier { property("title", hint) },
        )
    }
}

internal fun insertAtCursor(current: String, token: String): String {
    val el = kotlinx.browser.document.getElementById(PROMPT_TEXTAREA_ID) as? HTMLTextAreaElement
    val start = el?.selectionStart ?: current.length
    val end = el?.selectionEnd ?: current.length
    val before = current.substring(0, start.coerceIn(0, current.length))
    val after = current.substring(end.coerceIn(0, current.length))
    val next = before + token + after
    if (el != null) {
        val cursor = start + token.length
        el.value = next
        el.setSelectionRange(cursor, cursor)
        el.focus()
    }
    return next
}

private fun countVariables(text: String): Int =
    Regex("""\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}""").findAll(text).count()
